import { X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import type { DocumentUser } from "@shared/schema";

interface UserRowProps {
  user: DocumentUser;
  onSelect?: (user: DocumentUser) => void;
  showClickable?: boolean;
  onDelete?: (user: DocumentUser) => void;
  showDelete?: boolean;
  isAdmin?: boolean;
  documentColor?: string;
  showNotJoinedYet?: boolean;
}

export function UserRow({ user, onSelect, showClickable = false, onDelete, showDelete = false, isAdmin = false, documentColor, showNotJoinedYet = false }: UserRowProps) {
  const Component = showClickable && onSelect ? "button" : "div";

  const formatLastSeen = (lastSeenAt: string | Date | null) => {
    if (!lastSeenAt) return "Just joined";
    const date = lastSeenAt instanceof Date ? lastSeenAt : new Date(lastSeenAt);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const getFirstName = (fullName: string) => {
    return fullName.split(' ')[0];
  };
  
  return (
    <Component
      type={showClickable ? "button" : undefined}
      onClick={showClickable && onSelect ? () => onSelect(user) : undefined}
      data-testid={`user-row-${user.email}`}
      className={`w-full px-2.5 py-1.5 text-left rounded border border-border flex items-center justify-between gap-2 overflow-hidden ${
        showClickable 
          ? "hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer" 
          : ""
      }`}
    >
      <div className="flex items-baseline gap-1.5 min-w-0 flex-1">
        <span className="font-medium text-sm truncate min-w-0" data-testid={`user-name-${user.email}`}>
          {getFirstName(user.name)}
        </span>
        <span className="text-[10px] text-muted-foreground/60 truncate min-w-0" data-testid={`user-email-${user.email}`}>
          {user.email}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-none">
        <span className="text-[9px] text-muted-foreground/50 whitespace-nowrap" data-testid={`user-status-${user.email}`}>
          {showNotJoinedYet ? "Not joined" : formatLastSeen(user.lastSeenAt)}
        </span>
        {showDelete && onDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-5 w-5 p-0 hover:bg-accent flex-none"
                data-testid={`button-delete-user-${user.email}`}
                style={{
                  color: documentColor || undefined
                }}
              >
                <X className="h-3 w-3 opacity-60" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
              <AlertDialogHeader>
                <AlertDialogTitle>Remove User</AlertDialogTitle>
                <AlertDialogDescription>
                  Remove {user.name} from this document?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(user)}
                  className="bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </Component>
  );
}